<script setup>
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useForm } from '@inertiajs/vue3'
import UiButton from '@/Components/UI/UiButton.vue'
import UiInput from '@/Components/UI/UiInput.vue'
import UiSelect from '@/Components/UI/UiSelect.vue'
import Icon from '@/Components/UI/Icon.vue'
import { useCurrency } from '@/composables/useCurrency.js'
import { centsToInput, inputToCents } from '@/composables/useMoneyInput.js'

const props = defineProps({
  open:       { type: Boolean, default: false },
  plan:       { type: Object, default: null },
  categories: { type: Array, default: () => [] },
  vehicles:   { type: Array, default: () => [] },
})
const emit = defineEmits(['close'])

const { t } = useI18n()
const { formatMoney } = useCurrency()

const UNIT_MULTIPLIERS = { day: 1, week: 7, month: 30 }

const form = useForm({
  name:          '',
  duration_days: 1,
  total_cents:   0,
  category:      '',
  vehicle_ids:   [],
})

const unit  = ref('day')
const count = ref(1)
const planType = ref('category')

watch(() => props.plan, (p) => {
  form.name          = p?.name ?? ''
  form.duration_days = p?.duration_days ?? 1
  form.total_cents   = p?.total_cents ?? 0
  form.category      = p?.category ?? ''
  form.vehicle_ids   = p?.vehicle_ids ?? []
  planType.value     = p?.category ? 'category' : 'vehicle'
  if (p?.duration_days) {
    if (p.duration_days % 30 === 0) { unit.value = 'month'; count.value = p.duration_days / 30 }
    else if (p.duration_days % 7 === 0) { unit.value = 'week'; count.value = p.duration_days / 7 }
    else { unit.value = 'day'; count.value = p.duration_days }
  } else {
    unit.value = 'day'; count.value = 1
  }
}, { immediate: true })

watch([ unit, count ], () => {
  form.duration_days = Math.max(1, Number(count.value) * UNIT_MULTIPLIERS[unit.value])
})

const dailyHint = computed(() => {
  if (!form.duration_days) return null
  return Math.round(form.total_cents / form.duration_days)
})

const categoryOptions = computed(() => [
  { value: '', label: t('ratePlans.fields.category') + ' …' },
  ...(props.categories || []).map(c => ({ value: c, label: c }))
])

const unitOptions = computed(() => [
  { value: 'day',   label: t('ratePlans.units.day') },
  { value: 'week',  label: t('ratePlans.units.week') },
  { value: 'month', label: t('ratePlans.units.month') },
])

const switchType = (type) => {
  planType.value = type
  if (type === 'category') {
    form.vehicle_ids = []
  } else {
    form.category = ''
  }
}

const toggleVehicle = (id) => {
  const has = form.vehicle_ids.includes(id)
  form.vehicle_ids = has
    ? form.vehicle_ids.filter(x => x !== id)
    : [ ...form.vehicle_ids, id ]
}

const submit = () => {
  const payload = {
    rate_plan: {
      name:          form.name,
      duration_days: form.duration_days,
      total_cents:   form.total_cents,
      category:      planType.value === 'category' ? form.category : '',
    },
    vehicle_ids: planType.value === 'vehicle' ? form.vehicle_ids : [],
  }
  if (props.plan?.id) {
    form.transform(() => payload).patch(`/rate_plans/${props.plan.id}`, {
      preserveScroll: true,
      onSuccess: () => emit('close'),
    })
  } else {
    form.transform(() => payload).post('/rate_plans', {
      preserveScroll: true,
      onSuccess: () => emit('close'),
    })
  }
}
</script>

<template>
  <div
    v-if="open"
    class="fixed inset-0 z-50 flex md:items-center md:justify-center md:p-4 bg-black/60"
    role="dialog"
    aria-modal="true"
    @click.self="emit('close')"
  >
    <div class="w-full h-full md:h-auto md:max-w-md md:rounded-[var(--radius-lg)] md:shadow-[var(--shadow-lg)] bg-[color:var(--surface)] flex flex-col md:max-h-[calc(100vh-2rem)]">
      <header class="flex items-center justify-between p-4 border-b border-[color:var(--line)] flex-shrink-0">
        <h3 class="font-display text-base text-[color:var(--ink)]">
          {{ plan?.id ? t('ratePlans.edit') : t('ratePlans.new') }}
        </h3>
        <button
          type="button"
          class="text-[color:var(--muted)] hover:text-[color:var(--ink)]"
          :aria-label="t('common.close')"
          @click="emit('close')"
        >
          <Icon name="x" :size="20" />
        </button>
      </header>

      <form id="rate-plan-form" class="flex-1 overflow-y-auto p-4 space-y-4" @submit.prevent="submit">
        <div>
          <label class="block text-xs font-medium text-[color:var(--inkSoft)] mb-1">
            {{ t('ratePlans.fields.name') }} *
          </label>
          <UiInput v-model="form.name" type="text" required />
        </div>

        <div class="grid grid-cols-2 gap-3">
          <div>
            <label class="block text-xs font-medium text-[color:var(--inkSoft)] mb-1">
              {{ t('ratePlans.fields.duration') }}
            </label>
            <UiInput v-model.number="count" type="number" min="1" />
          </div>
          <div>
            <label class="block text-xs font-medium text-[color:var(--inkSoft)] mb-1">
              {{ t('ratePlans.fields.durationUnit') }}
            </label>
            <UiSelect v-model="unit" :options="unitOptions" />
          </div>
        </div>

        <div>
          <label class="block text-xs font-medium text-[color:var(--inkSoft)] mb-1">
            {{ t('ratePlans.fields.totalPrice') }} *
          </label>
          <UiInput
            :model-value="centsToInput(form.total_cents)"
            type="number"
            min="0"
            step="any"
            @update:model-value="form.total_cents = inputToCents($event)"
          />
          <p v-if="dailyHint" class="mt-1 text-xs text-[color:var(--muted)]">
            {{ t('ratePlans.fields.dailyHint', { rate: formatMoney(dailyHint) }) }}
          </p>
        </div>

        <div>
          <label class="block text-xs font-medium text-[color:var(--inkSoft)] mb-1">
            {{ t('ratePlans.fields.type') }}
          </label>
          <div class="flex gap-2">
            <button
              type="button"
              class="flex-1 px-3 py-2 rounded-[var(--radius-md)] border text-sm transition-colors"
              :style="planType === 'category'
                ? { backgroundColor: 'var(--accent)', color: 'var(--accentInk)', borderColor: 'var(--accent)' }
                : { borderColor: 'var(--line)', color: 'var(--ink)' }"
              @click="switchType('category')"
            >
              {{ t('ratePlans.types.category') }}
            </button>
            <button
              type="button"
              class="flex-1 px-3 py-2 rounded-[var(--radius-md)] border text-sm transition-colors"
              :style="planType === 'vehicle'
                ? { backgroundColor: 'var(--accent)', color: 'var(--accentInk)', borderColor: 'var(--accent)' }
                : { borderColor: 'var(--line)', color: 'var(--ink)' }"
              @click="switchType('vehicle')"
            >
              {{ t('ratePlans.types.vehicleSpecific') }}
            </button>
          </div>
        </div>

        <div v-if="planType === 'category'">
          <label class="block text-xs font-medium text-[color:var(--inkSoft)] mb-1">
            {{ t('ratePlans.fields.category') }} *
          </label>
          <UiSelect v-model="form.category" :options="categoryOptions" />
        </div>

        <div v-else>
          <label class="block text-xs font-medium text-[color:var(--inkSoft)] mb-1">
            {{ t('ratePlans.fields.assignedVehicles') }}
          </label>
          <ul class="max-h-64 overflow-y-auto border border-[color:var(--line)] rounded-[var(--radius-md)] divide-y divide-[color:var(--line)]">
            <li
              v-for="v in vehicles"
              :key="v.id"
              class="px-3 py-2 flex items-center gap-2 cursor-pointer hover:bg-[color:var(--surfaceAlt)]"
              @click="toggleVehicle(v.id)"
            >
              <input
                type="checkbox"
                :checked="form.vehicle_ids.includes(v.id)"
                class="rounded"
                @click.stop="toggleVehicle(v.id)"
              >
              <span class="text-sm text-[color:var(--ink)] flex-1 truncate">{{ v.label }}</span>
              <span class="text-xs text-[color:var(--muted)] capitalize">{{ v.category }}</span>
            </li>
            <li v-if="!vehicles.length" class="px-3 py-4 text-sm text-[color:var(--muted)] text-center">
              {{ t('vehicles.emptyFirst') }}
            </li>
          </ul>
        </div>
      </form>

      <footer class="flex items-center justify-end gap-2 p-4 border-t border-[color:var(--line)] flex-shrink-0">
        <UiButton variant="secondary" @click="emit('close')">{{ t('common.cancel') }}</UiButton>
        <UiButton
          variant="primary"
          type="submit"
          form="rate-plan-form"
          :disabled="!form.name || form.duration_days < 1 || form.processing"
        >
          {{ t('common.save') }}
        </UiButton>
      </footer>
    </div>
  </div>
</template>
